import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "flowbite-react";
import { AiOutlineLoading } from "react-icons/ai";
import { NavBar, SideNav } from "../../components";
import Breadcrumb from "../../components/Breadcrumb";
import { fetchAiUsage } from "../../redux/slices/aiUsage";

const isoDate = (d) => d.toISOString().slice(0, 10);

const defaultFrom = () => {
  const d = new Date();
  d.setDate(d.getDate() - 14);
  return isoDate(d);
};

const UsageBarChart = ({ series }) => {
  const width = 640;
  const height = 200;
  const pad = { top: 16, right: 12, bottom: 36, left: 40 };
  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;

  const maxReq = Math.max(1, ...series.map((s) => s.requests || 0));
  const barGap = 4;
  const barW =
    series.length > 0
      ? Math.max(2, (innerW - barGap * (series.length - 1)) / series.length)
      : 0;

  if (series.length === 0) {
    return (
      <p className="text-sm text-gray-500 italic py-8 text-center">
        No usage data for this range.
      </p>
    );
  }

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="w-full h-auto"
      role="img"
      aria-label="AI usage requests over time"
    >
      {[0, 0.25, 0.5, 0.75, 1].map((t) => {
        const y = pad.top + innerH * (1 - t);
        const val = Math.round(maxReq * t);
        return (
          <g key={t}>
            <line
              x1={pad.left}
              x2={width - pad.right}
              y1={y}
              y2={y}
              stroke="#e5e7eb"
              strokeWidth="1"
            />
            <text
              x={pad.left - 6}
              y={y + 3}
              textAnchor="end"
              className="fill-gray-400"
              fontSize="10"
            >
              {val}
            </text>
          </g>
        );
      })}
      {series.map((row, i) => {
        const h = ((row.requests || 0) / maxReq) * innerH;
        const x = pad.left + i * (barW + barGap);
        const y = pad.top + innerH - h;
        const label =
          series.length <= 16 || i % Math.ceil(series.length / 8) === 0
            ? String(row.date || "").slice(5)
            : "";
        return (
          <g key={row.date || i}>
            <rect
              x={x}
              y={y}
              width={barW}
              height={Math.max(h, 0)}
              fill="#7c3aed"
              rx="2"
            >
              <title>
                {row.date}: {row.requests || 0} requests
              </title>
            </rect>
            {label && (
              <text
                x={x + barW / 2}
                y={height - 12}
                textAnchor="middle"
                className="fill-gray-500"
                fontSize="9"
              >
                {label}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
};

const AiUsage = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { series, totals, byModel, pageLoading, error } = useSelector(
    (state) => state.aiUsage
  );
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(() => isoDate(new Date()));

  const isAdmin = user && user.role === "admin";

  useEffect(() => {
    if (isAdmin) {
      dispatch(fetchAiUsage({ from, to }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isAdmin]);

  const modelRows = useMemo(
    () =>
      Object.entries(byModel || {}).map(([model, stats]) => ({
        model,
        ...stats,
      })),
    [byModel]
  );

  if (!isAdmin) {
    return (
      <div className="antialiased bg-gray-50 dark:bg-gray-900 min-h-screen">
        <NavBar />
        <SideNav />
        <main className="p-4 md:ml-64 h-auto pt-20">
          <Breadcrumb />
          <div className="mt-6 p-6 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-white">
              AI Usage
            </h2>
            <p className="mt-2 text-sm text-gray-500">
              Admin access required to view AI usage metrics.
            </p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="antialiased bg-gray-50 dark:bg-gray-900 min-h-screen">
      <NavBar />
      <SideNav />
      <main className="p-4 md:ml-64 h-auto pt-20">
        <div className="sticky top-14 z-10 bg-gray-50 dark:bg-gray-900">
          <Breadcrumb />
        </div>

        <section className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
              AI Usage
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Daily request, token, and estimated cost metrics.
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <label className="text-sm text-gray-600">
              From
              <input
                type="date"
                className="mt-1 block rounded-lg border-gray-300 text-sm"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </label>
            <label className="text-sm text-gray-600">
              To
              <input
                type="date"
                className="mt-1 block rounded-lg border-gray-300 text-sm"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </label>
            <Button
              color="purple"
              disabled={pageLoading}
              isProcessing={pageLoading}
              processingSpinner={
                <AiOutlineLoading className="h-5 w-5 animate-spin" />
              }
              onClick={() => dispatch(fetchAiUsage({ from, to }))}
            >
              Fetch
            </Button>
          </div>
        </section>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 border border-red-100 text-sm">
            {String(error)}
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[
            { label: "Requests", value: totals?.requests ?? "—" },
            { label: "Tokens in", value: totals?.tokensIn ?? "—" },
            { label: "Tokens out", value: totals?.tokensOut ?? "—" },
            {
              label: "Est. cost (USD)",
              value:
                totals?.estCostUSD != null
                  ? Number(totals.estCostUSD).toFixed(4)
                  : "—",
            },
          ].map((card) => (
            <div
              key={card.label}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4"
            >
              <p className="text-xs uppercase tracking-wide text-gray-500">
                {card.label}
              </p>
              <p className="mt-1 text-2xl font-semibold text-gray-800 dark:text-white">
                {card.value}
              </p>
            </div>
          ))}
        </div>

        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 mb-6">
          <h3 className="font-semibold text-gray-800 dark:text-white mb-3">
            Requests over time
          </h3>
          {pageLoading && series.length === 0 ? (
            <div className="py-10 text-center">
              <AiOutlineLoading className="inline h-6 w-6 animate-spin text-purple-600" />
            </div>
          ) : (
            <UsageBarChart series={series} />
          )}
        </div>

        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700">
            <h3 className="font-semibold text-gray-800 dark:text-white">
              By model
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
              <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                <tr>
                  <th className="px-6 py-3">Model</th>
                  <th className="px-6 py-3">Requests</th>
                  <th className="px-6 py-3">Tokens in</th>
                  <th className="px-6 py-3">Tokens out</th>
                  <th className="px-6 py-3">Est. cost</th>
                </tr>
              </thead>
              <tbody>
                {modelRows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-6 text-center">
                      No model breakdown.
                    </td>
                  </tr>
                ) : (
                  modelRows.map((row, i) => (
                    <tr
                      key={row.model}
                      className={`border-b dark:border-gray-700 ${
                        i % 2 === 0 ? "bg-white" : "bg-gray-50"
                      } dark:bg-gray-800`}
                    >
                      <td className="px-6 py-4 font-medium text-gray-900 dark:text-white">
                        {row.model}
                      </td>
                      <td className="px-6 py-4">{row.requests ?? 0}</td>
                      <td className="px-6 py-4">{row.tokensIn ?? 0}</td>
                      <td className="px-6 py-4">{row.tokensOut ?? 0}</td>
                      <td className="px-6 py-4">
                        {row.estCostUSD != null
                          ? Number(row.estCostUSD).toFixed(4)
                          : "—"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AiUsage;
